import { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { CoinsIcon, SearchIcon } from '../../assets/Icon';
import BurnTokenModal from '../../components/dialogs/burn-token-dialog';
import MintTokenModal from '../../components/dialogs/mint-token-dialog';
import ReceiveTokenModal from '../../components/dialogs/receive-token-dialog';
import TableComponent from '../../components/Table';
import CustomDateRangePicker from '../../components/common/DatePicker/CustomDateRangePicker';
import Skeletone from '../../components/ui/Skeletone';
import TransactionLink from '../../components/ui/TransactionLink';

import AuthContext from '../../contexts/AuthContext';
import TokenListContext from '../../contexts/TokenListContext';

import useAPI from '../../hooks/useAPI';
import { formatCurrency } from '../../utility/number.utils';
import styles from './style.module.scss';
import axios from 'axios';
import apiInstance from '../../config/axios';
import { convertToCSV } from '../../lib/lib';

interface IPaginationObject {
  limit: number;
  page: number;
  search: string;
  status: string;
  type: string;
  from: string;
  to: string;
}

interface ITransactionResponse {
  result: {
    tokenTransactionHistory: any[];
    totalTokenTransactionHistory: number;
    totalPages: number;
  };
}

const statusOptions = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Success', value: 'SUCCESS' },
  { label: 'Failed', value: 'FAILED' },
];

const transactionTypeOptions = [
  { label: 'Mint', value: 'MINT' },
  { label: 'Burn', value: 'BURN' },
  { label: 'Transfer', value: 'TRANSFER' },
  { label: 'Receive', value: 'RECEIVE' },
];

const MAX_DATE_RANGE_DAYS = 90;

export default function TokenDashboard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState({ isOpen: false, type: '' });
  const [isDownloading, setIsDownloading] = useState(false);

  // Filter states
  const [paginationData, setPaginationData] = useState<IPaginationObject>({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    type: '',
    from: '',
    to: '',
  });

  // Date range states
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string>('');

  // Search debounce
  const [searchInput, setSearchInput] = useState('');

  const {
    tokenList,
    totalToken,
    isLoading: isTokenLoading,
  } = useContext(TokenListContext);

  const { adminBalance, refetchAdminBalance, isAdminBalanceLoading } =
    useContext(AuthContext);

  // Build API URL with query parameters
  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationData.page)); // API uses 0-based page
    params.append('limit', String(paginationData.limit));
    params.append('status', paginationData.status);
    params.append('type', paginationData.type);
    params.append('from', paginationData.from);
    params.append('to', paginationData.to);
    params.append('search', paginationData.search);
    return `/token/transaction/history/get?${params.toString()}`;
  };

  // Fetch transaction history using useAPI
  const {
    data: transactionResponse,
    isLoading: isTransactionLoading,
    refetch: refetchTransactions,
  } = useAPI<void, ITransactionResponse>({
    url: buildApiUrl(),
    method: 'GET',
    dependencies: [
      paginationData.page,
      paginationData.limit,
      paginationData.status,
      paginationData.type,
      paginationData.from,
      paginationData.to,
      paginationData.search,
    ],
    onSuccess: () => {},
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch transactions',
      );
    },
  });

  const transactionData =
    transactionResponse?.result?.tokenTransactionHistory || [];
  const totalCount =
    transactionResponse?.result?.totalTokenTransactionHistory || 0;

  // Validate date range (max 90 days)
  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined,
  ): boolean => {
    if (!start || !end) return true;

    const diffInDays = moment(end).diff(moment(start), 'days');
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }
    setDateError('');
    return true;
  };

  // Handle date range change with validation
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      if (validateDateRange(startDate, date)) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          from: moment(startDate).format('YYYY-MM-DD'),
          to: moment(date).format('YYYY-MM-DD'),
        }));
      }
    } else if (!date) {
      setDateError('');
      setPaginationData(prev => ({
        ...prev,
        page: 1,
        from: '',
        to: '',
      }));
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== paginationData.search) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          search: searchInput,
        }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Handle status filter change
  const handleStatusChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      status: value === 'all' ? '' : value,
    }));
  };

  // Handle type filter change
  const handleTypeChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      type: value === 'all' ? '' : value,
    }));
  };

  // Handle pagination change from TableComponent
  const handleFetchMoreData = (pageSize: number, page: number) => {
    setPaginationData(prev => ({
      ...prev,
      limit: pageSize,
      page: page,
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setDateRange([null, null]);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateError('');
    setPaginationData({
      page: 1,
      limit: 10,
      search: '',
      status: '',
      type: '',
      from: '',
      to: '',
    });
  };

  const handelClose = (withSuccess?: boolean) => {
    if (withSuccess) {
      refetchTransactions();
      refetchAdminBalance();
    }
    setOpen({ isOpen: false, type: '' });
  };

  useEffect(() => {
    if (adminBalance) {
      refetchTransactions();
    }
  }, [adminBalance]);

  const goToTransferPage = () => navigate('/token-management/transfer-token');
  const goToMintTokenPage = () => {
    setOpen({ isOpen: true, type: 'mint' });
  };

  useEffect(() => {
    if (totalToken == 0 && !isTokenLoading) {
      navigate('/token-management/mint-token');
    }
  }, [totalToken, isTokenLoading]);

  useEffect(() => {
    refetchAdminBalance();
  }, []);

  const getStatusValue = (tokenType: string, status: string) => {
    if (tokenType === 'RECEIVE') {
      if (status === 'PENDING') return 'REQUESTED';
      if (status === 'SUCCESS') return 'ACCEPTED';
      if (status === 'FAILED') return 'REJECTED';
    } else if (tokenType === 'TRANSFER') {
      return 'TRANSFERRED';
    } else if (tokenType === 'MINT') {
      return 'MINTED';
    } else if (tokenType === 'BURN') {
      return 'BURNT';
    } else {
      return tokenType;
    }
  };

  const transactionTableColumns = [
    {
      label: 'Transaction No.',
      id: 'transactionId',
      width: 300,
      renderCell: (row: any) => {
        return row?.transactionNo ? (
          <TransactionLink transactionNo={row?.transactionNo} />
        ) : (
          '-'
        );
      },
    },
    {
      label: '# of Tokens',
      id: 'tokenAmount',
      renderCell: (row: any) => (
        <span>
          {formatCurrency(row?.tokenAmount ?? 0, {
            currency: '',
            decimals: 0,
          })}
        </span>
      ),
    },
    {
      label: 'Transaction Date & Time',
      id: 'transactionDate',
      renderCell: (row: any) =>
        row?.transactionDate
          ? moment(row.transactionDate).format('MMM D YYYY HH:mm:ss')
          : '-',
    },
    {
      label: 'Created By ',
      id: 'receiverFirstName',
      renderCell: (row: any) =>
        row?.firstName + ' ' + row?.lastName + ' , ' + row?.createdByEmail,
    },
    {
      label: 'Received By',
      id: 'receiverFirstName',
      renderCell: (row: any) =>
        row?.receiverFirstName +
        ' ' +
        row?.receiverLastName +
        ' , ' +
        row?.receiverByEmail,
    },
    // {
    //   label: 'Receiver By',
    //   id: 'receiverByEmail',
    //   renderCell: (row: any) => row?.receiverByEmail,
    // },
    {
      label: 'Type',
      id: 'tokenType',
      renderCell: (rowData: any) => (
        <span
          className={`${rowData?.tokenType === 'BURN' ? 'text-danger' : ''} custom-text-left`}
        >
          {/* {rowData?.tokenType + ' - ' + rowData?.type} */}
          {getStatusValue(rowData?.tokenType, rowData?.status)}
        </span>
      ),
    },
    {
      label: 'Status',
      id: 'tokenType',
      renderCell: (rowData: any) => (
        <span
          className={`${rowData?.tokenType === 'BURN' ? 'text-danger' : ''} custom-text-left`}
        >
          {/* {rowData?.tokenType + ' - ' + rowData?.type} */}
          {rowData?.status}
        </span>
      ),
    },
  ];

  const hasActiveFilters =
    paginationData.search ||
    paginationData.status ||
    paginationData.type ||
    paginationData.from ||
    paginationData.to;

  // Convert array of objects to CSV string
  // const convertToCSV = (data: any[]): string => {
  //   if (!data || data.length === 0) return '';

  //   const headers = Object.keys(data[0]);
  //   const csvRows = [];

  //   // Add header row
  //   csvRows.push(headers.join(','));

  //   // Add data rows
  //   for (const row of data) {
  //     const values = headers.map(header => {
  //       const value = row[header];
  //       // Handle values with commas, quotes, or newlines
  //       const escaped = String(value ?? '').replace(/"/g, '""');
  //       return `"${escaped}"`;
  //     });
  //     csvRows.push(values.join(','));
  //   }

  //   return csvRows.join('\n');
  // };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', paginationData.status || '');
      params.append('from', paginationData.from);
      params.append('to', paginationData.to);
      params.append('type', paginationData.type || '');
      params.append('search', paginationData.search || '');

      const response = await apiInstance.get(
        `/token/transaction/history/download?${params.toString()}`,
      );

      const transactionHistory = response.data.result.requestedData;

      if (!transactionHistory || transactionHistory.length === 0) {
        toast.error('No data available to download');
        return;
      }

      const csvContent = convertToCSV(transactionHistory);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = moment().format('DDMMYYYYHHmmss');
      link.setAttribute('download', `TokenManagement${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error(error?.response?.data?.message || 'Failed to download CSV');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between">
        <div>
          <h2 className="fw-bold">Token Management</h2>
        </div>
        <div className="d-flex justify-content-center">
          <div className="d-flex gap-2 align-items-center">
            <Form.Group>
              <Button
                variant="outline-secondary border-2 rounded-2 text-dark bg-white fw-bold"
                onClick={goToMintTokenPage}
              >
                Mint New Tokens
              </Button>
            </Form.Group>
            <Form.Group>
              <Button
                onClick={() => setOpen({ isOpen: true, type: 'burn' })}
                variant="outline-secondary border-2 rounded-2 text-dark bg-white fw-bold"
              >
                Burn Tokens
              </Button>
            </Form.Group>
            <Form.Group>
              <Button
                variant="outline-secondary border-2 rounded-2 text-dark bg-white fw-bold"
                onClick={goToTransferPage}
              >
                Transfer Tokens
              </Button>
            </Form.Group>
          </div>
        </div>

        <div className="w-100">
          <Row className="my-3 g-3">
            <Col xs={12} lg={9}>
              <div
                className={`${styles.statsContainer} rounded-3 d-flex flex-column flex-md-row gap-3 align-items-stretch align-items-md-center bg-white`}
              >
                {isAdminBalanceLoading ? (
                  <Skeletone divider={true} />
                ) : (
                  <div
                    className={`${styles.mintedTokens} p-3 p-md-4 d-flex flex-column flex-md-row flex-auto gap-2 gap-md-3 align-items-start align-items-md-center`}
                  >
                    <div className="d-flex align-items-center">
                      <div
                        className={`${styles.coinsIcon} p-3 p-md-4 rounded-circle`}
                      >
                        <CoinsIcon size={35} color={'var(--primary)'} />
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <p className="mb-0 fw-semibold text-grey small">
                        Total Minted Tokens
                      </p>
                      <p className={`mb-0 fw-bold ${styles.responsiveHeading}`}>
                        {formatCurrency(adminBalance?.totalMint ?? 0, {
                          currency: '',
                          decimals: 0,
                        })}
                      </p>
                      <p className="mb-0 small">
                        Last Minted :{' '}
                        <strong>
                          {formatCurrency(adminBalance?.LastMintToken ?? 0, {
                            currency: '',
                            decimals: 0,
                          })}
                        </strong>
                      </p>
                    </div>
                    <div className="align-self-stretch align-self-md-baseline d-flex align-items-center">
                      <Button
                        onClick={goToMintTokenPage}
                        variant="outline-secondary border-2 rounded-2 text-dark bg-white fw-bold"
                        size="sm"
                        className="w-100 w-md-auto"
                      >
                        Mint New Tokens
                      </Button>
                    </div>
                  </div>
                )}
                {isAdminBalanceLoading ? (
                  <Skeletone />
                ) : (
                  <div className={`${styles.transferredTokens} p-3 p-md-4`}>
                    <p className="mb-0 fw-semibold text-grey small">
                      Total Transferred Tokens
                    </p>
                    <p className={`mb-0 fw-bold ${styles.responsiveHeading}`}>
                      {formatCurrency(adminBalance?.totalTransfer ?? 0, {
                        currency: '',
                        decimals: 0,
                      })}
                    </p>
                    <p className="mb-0 small">
                      Last Transferred :{' '}
                      <strong>
                        {formatCurrency(adminBalance?.lastTransferTokens ?? 0, {
                          currency: '',
                          decimals: 0,
                        })}
                      </strong>
                    </p>
                  </div>
                )}
                {isAdminBalanceLoading ? (
                  <Skeletone />
                ) : (
                  <div className={`${styles.totalBurn} p-3 p-md-4`}>
                    <p className="mb-0 fw-semibold text-grey small">
                      Total Burned Tokens
                    </p>
                    <p className={`mb-0 fw-bold ${styles.responsiveHeading}`}>
                      {formatCurrency(adminBalance?.totalBurn ?? 0, {
                        currency: '',
                        decimals: 0,
                      })}
                    </p>
                  </div>
                )}
              </div>
            </Col>
            <Col xs={12} lg={3}>
              {isAdminBalanceLoading ? (
                <Skeletone />
              ) : (
                <div className="p-3 p-md-4 rounded-3 d-flex align-items-center bg-dark gap-2 gap-md-3 h-100">
                  <div>
                    <div
                      className={`${styles.coinsIcon} p-3 p-md-4 rounded-circle`}
                    >
                      <CoinsIcon size={35} color={'var(--primary)'} />
                    </div>
                  </div>
                  <div className="text-white flex-grow-1">
                    <p className="mb-0 small">Available Tokens</p>
                    <p
                      className={`mb-0 fw-bold ${styles.responsiveHeadingLarge}`}
                    >
                      {formatCurrency(adminBalance?.UserBalance ?? 0, {
                        currency: '',
                        decimals: 0,
                      })}
                    </p>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </div>

        <div className="w-100 bg-white rounded-3 p-3">
          {/* Filters Section */}
          <div className="d-flex flex-wrap gap-3 mb-3 align-items-start">
            {/* Search Input */}
            <div
              className="flex-grow-1"
              style={{ minWidth: '200px', maxWidth: '300px' }}
            >
              <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <SearchIcon size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name and  email"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="border-start-0 custom-input"
                />
              </InputGroup>
            </div>

            {/* Date Range Picker */}
            <div style={{ minWidth: '220px' }} className="d-flex flex-column">
              <Form.Label className="small fw-semibold mb-1">
                Date Range <span className="text-muted fw-normal"></span>
              </Form.Label>
              <CustomDateRangePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                // @ts-ignore
                setEndDate={handleEndDateChange}
                placeholder="Select date range"
              />
              {dateError && (
                <Form.Text className="text-danger small">{dateError}</Form.Text>
              )}
            </div>

            {/* Transaction Type Filter */}
            <div style={{ minWidth: '150px' }}>
              <Form.Label className="small fw-semibold mb-1">Type</Form.Label>
              <Form.Select
                className="rounded-2 custom-input"
                value={paginationData.type || 'all'}
                onChange={e => handleTypeChange(e.target.value)}
              >
                <option value="all">All Types</option>
                {transactionTypeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Status Filter */}
            <div style={{ minWidth: '150px' }}>
              <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
              <Form.Select
                className="rounded-2 custom-input"
                value={paginationData.status || 'all'}
                onChange={e => handleStatusChange(e.target.value)}
              >
                <option value="all">All Status</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="d-flex flex-column">
                <Form.Label className="small fw-semibold mb-1 custom-filter">
                  {/* <span className="text-muted fw-normal" style={{ fontSize: '11px' }}>
              
            </span> */}
                </Form.Label>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleClearFilters}
                  className="rounded-2 custom-filter-button"
                >
                  Clear Filters
                </Button>
              </div>
            )}

            {/* Download Button */}
            <div className="d-flex flex-column">
              <Form.Label className="small fw-semibold mb-1">&nbsp;</Form.Label>
              <Button
                className={styles.actionButton}
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </div>
          </div>

          {/* Transaction Table */}
          <div className={styles.tableContainer}>
            <TableComponent
              data={transactionData}
              columns={transactionTableColumns}
              totalCount={totalCount}
              fetchMore={handleFetchMoreData}
              isServerSidePagination
              perPageSize={paginationData.limit}
              activePage={paginationData.page}
              loading={isTransactionLoading}
              customBodyCss="custom-table-token-dashboard"
            />
          </div>
        </div>

        <BurnTokenModal
          tokenId={tokenList?.[0]?.tokenId}
          open={open.isOpen && open.type === 'burn'}
          onClose={handelClose}
          refetchTransactionHistory={refetchTransactions}
        />
        <ReceiveTokenModal
          tokenId={tokenList?.[0]?.tokenId}
          open={open.isOpen && open.type === 'receive'}
          onClose={handelClose}
        />
        <MintTokenModal
          tokenId={tokenList?.[0]?.tokenId}
          open={open.isOpen && open.type === 'mint'}
          onClose={handelClose}
          refetchTransactionHistory={refetchTransactions}
        />
      </div>
    </>
  );
}
